import { useAuth } from "@/hooks/miscellaneous/useAuth";
import { useAuth0 } from "@auth0/auth0-react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useGetUserQuery } from "./hooks/queries/useGetUserQuery";
import { useEffect } from "react";
import useAnalyticsManager from "./hooks/miscellaneous/useAnalyticsManager";
import { useGetRolesQuery } from "./hooks/queries";
import { getCookie } from "@/lib/cookie";
import { authRecoveryRef } from "@/auth/authRecoveryRef";
import { FullScreenLoader } from "@/components/common/FullScreenLoader";

export const RequireAuth = () => {
  const { isAuthenticated: auth0IsAuthenticated, isLoading: auth0IsLoading } =
    useAuth0();
  const location = useLocation();

  // While Auth0 is initializing, fall back to cookie presence to avoid a
  // flash of /auth/signin on page refresh.
  const cookieHasToken = Boolean(getCookie("accessToken"));
  const isAuthenticated = auth0IsLoading ? cookieHasToken : auth0IsAuthenticated;
  const {
    data: roleData,
    isFetching,
    isError,
    isFetched,
  } = useGetRolesQuery({ enabled: isAuthenticated === true });
  const { identifyUser, setUserProperties } = useAnalyticsManager();

  const { data: user } = useGetUserQuery({
    enabled: isAuthenticated === true,
  });

  // Roles settled but produced nothing usable — the session is broken even
  // though Auth0 still reports isAuthenticated (stale cached ID token). Drive
  // recovery instead of falling through to a <Navigate> that renders nothing.
  const rolesUnusable =
    isAuthenticated === true && !isFetching && (isError || (isFetched && !roleData));

  useEffect(() => {
    if (rolesUnusable) {
      void authRecoveryRef.current?.();
    }
  }, [rolesUnusable]);

  useEffect(() => {
    if (user) {
      identifyUser(user.id.toString());
      setUserProperties(
        user.firstName,
        user.lastName,
        user.email,
        roleData?.roles,
        user.tenantName,
        user.tenantId
      );
    }
  }, [user]);

  if (isAuthenticated === false) {
    return (
      <Navigate to="/auth/signin" replace state={{ path: location.pathname }} />
    );
  }

  if (rolesUnusable) {
    return <FullScreenLoader message="Signing you in…" />;
  }

  if (isFetching || !isFetched) {
    return <FullScreenLoader />;
  }

  if (roleData?.isAdmin || roleData?.isSuperAdmin) {
    return <Outlet />;
  }

  const isRegularUser = roleData?.roles?.some(
    (role) =>
      role === "USER" ||
      role === "OWNER" ||
      role.endsWith("_USER") ||
      role.endsWith("_OWNER")
  );

  if (isRegularUser) {
    if (location.pathname !== "/insights") {
      return <Navigate to="/insights" replace />;
    }
    return <Outlet />;
  }

  // roleData exists but carries no recognised role. Not an auth failure, so
  // don't trigger recovery — but never navigate onto the current path, which
  // renders nothing.
  if (location.pathname !== "/insights") {
    return <Navigate to="/insights" replace />;
  }
  return <Outlet />;
};

export const GuestRoute = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  return isAuthenticated === false ? (
    <Outlet />
  ) : (
    <Navigate to="/" replace state={{ path: location.pathname }} />
  );
};
